import { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  MonitorSpeaker, LogOut, Megaphone, RotateCcw, Check, Trash2, Plus,
  DoorOpen, Users, Settings as SettingsIcon, ListChecks, ExternalLink, Copy, UserPlus,
  Upload, Image as ImageIcon, Loader2, ListPlus, Search, Youtube, Film, Clock,
  BarChart3, Timer, ClipboardPaste, Hourglass, FileSpreadsheet, Sparkles, Crown, CreditCard, SkipForward, MessageCircle,
} from "lucide-react";
import { UpgradeCTA } from "@/components/UpgradeCTA";
import { uploadMediaChunked } from "@/lib/upload";
import { parseYouTube } from "@/lib/media";
import { VoiceSettings } from "@/components/VoiceSettings";
import { MediaVolume } from "@/components/MediaVolume";
import { BrandIcon } from "@/components/Brand";
import { TvCodeCard, TvCodeChip } from "@/components/TvCode";

const ROLE_LABELS = { owner: "Dono", reception: "Recepção", professional: "Profissional" };

function timeAgo(iso, nowTs) {
  if (!iso) return "";
  const secs = Math.max(0, Math.floor((nowTs - new Date(iso).getTime()) / 1000));
  const m = Math.floor(secs / 60);
  if (m < 1) return "agora";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h${mm > 0 ? ` ${mm}min` : ""}`;
}

function WaitTimer({ since, prefix = "", className = "" }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);
  return <span className={className}>{prefix}{timeAgo(since, now)}</span>;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [queue, setQueue] = useState({ waiting: [], called: [] });
  const [clinic, setClinic] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [tab, setTab] = useState("queue");

  const canManage = user.role === "owner" || user.role === "reception";

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      if (data && Array.isArray(data.waiting) && Array.isArray(data.called)) setQueue(data);
    } catch (e) {}
  }, []);

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await api.get("/rooms");
      if (Array.isArray(data)) setRooms(data);
    } catch (e) {}
  }, []);

  const loadClinic = useCallback(async () => {
    try {
      const { data } = await api.get("/clinic");
      if (data && typeof data === "object" && data.id) setClinic(data);
    } catch (e) {}
  }, []);

  const loadSubscription = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription");
      if (!data || typeof data !== "object") return;
      setSubscription(data);
      if (data.blocked) setBlocked(true);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadRooms();
    loadClinic();
    loadQueue();
    loadSubscription();
    const t = setInterval(loadQueue, 3000);
    const onVisible = () => {
      if (document.visibilityState === "visible") { loadQueue(); loadRooms(); loadClinic(); loadSubscription(); }
    };
    document.addEventListener("visibilitychange", onVisible);
    const onBlocked = () => setBlocked(true);
    const onUpgrade = () => { setTab("assinatura"); window.scrollTo({ top: 0, behavior: "smooth" }); };
    window.addEventListener("subscription-blocked", onBlocked);
    window.addEventListener("open-upgrade", onUpgrade);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("subscription-blocked", onBlocked);
      window.removeEventListener("open-upgrade", onUpgrade);
    };
  }, [loadRooms, loadClinic, loadQueue, loadSubscription]);

  const tvUrl = clinic ? `${window.location.origin}/tv/${clinic.id}` : "";
  const mediaCount = (clinic?.media_items || []).length + (clinic?.video_url ? 1 : 0);
  const [skipping, setSkipping] = useState(false);
  const skipTvMedia = async () => {
    setSkipping(true);
    try {
      await api.post("/tv/skip-media");
      toast.success("Próxima mídia enviada para a TV");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setTimeout(() => setSkipping(false), 1200); }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandIcon className="w-10 h-10" testid="dashboard-brand-icon" />
            <div className="leading-tight">
              <p className="font-heading font-bold text-ink">{clinic?.name || "O Seu Chamador"}</p>
              <p className="text-xs text-cappuccino">{user.name} · {ROLE_LABELS[user.role]}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {clinic && <TvCodeChip />}
            {clinic && canManage && mediaCount > 0 && <MediaVolume initial={clinic.media_volume} />}
            {clinic && canManage && mediaCount > 1 && (
              <Button variant="outline" onClick={skipTvMedia} disabled={skipping} data-testid="skip-tv-media-button"
                title="Pula a imagem ou vídeo que está passando na TV" className="rounded-md gap-2 border-border text-ink hover:bg-accent">
                {skipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <SkipForward className="w-4 h-4" />}
                <span className="hidden sm:inline">Pular mídia da TV</span>
              </Button>
            )}
            {clinic && (
              <a href={tvUrl} target="_blank" rel="noreferrer" data-testid="open-tv-link">
                <Button variant="outline" className="rounded-md gap-2 border-gold text-ink hover:bg-accent">
                  <ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">Abrir TV</span>
                </Button>
              </a>
            )}
            <Button data-testid="logout-button" variant="ghost" onClick={logout} className="rounded-md gap-2 text-cappuccino">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      {subscription?.subscription_status === "trialing" && subscription.trial_days_left != null && !blocked && (
        <div className="bg-ink text-cream text-sm px-5 sm:px-8 py-2.5 flex items-center justify-center gap-3" data-testid="trial-banner">
          <Clock className="w-4 h-4 text-gold" />
          <span>Teste grátis — <strong>{subscription.trial_days_left} dia(s)</strong> restante(s)</span>
          {user.role === "owner" && (
            <button onClick={() => setTab("assinatura")} className="underline font-semibold text-gold" data-testid="banner-subscribe">Assinar agora</button>
          )}
        </div>
      )}

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent rounded-none p-0 h-auto flex-wrap gap-1 w-full justify-start border-b border-cappuccino/20">
            <TabsTrigger data-testid="tab-queue" value="queue" className="rounded-none -mb-px gap-2 border-b-2 border-transparent text-cappuccino data-[state=active]:bg-transparent data-[state=active]:border-gold data-[state=active]:text-ink data-[state=active]:shadow-none px-4 py-2.5">
              <ListChecks className="w-4 h-4" /> Fila
            </TabsTrigger>
            {canManage && subscription && (
              <TabsTrigger data-testid="tab-reports" value="reports" className="rounded-none -mb-px gap-2 border-b-2 border-transparent text-cappuccino data-[state=active]:bg-transparent data-[state=active]:border-gold data-[state=active]:text-ink data-[state=active]:shadow-none px-4 py-2.5">
                <BarChart3 className="w-4 h-4" /> Relatórios
              </TabsTrigger>
            )}
            {canManage && (
              <TabsTrigger data-testid="tab-rooms" value="rooms" className="rounded-none -mb-px gap-2 border-b-2 border-transparent text-cappuccino data-[state=active]:bg-transparent data-[state=active]:border-gold data-[state=active]:text-ink data-[state=active]:shadow-none px-4 py-2.5">
                <DoorOpen className="w-4 h-4" /> Salas
              </TabsTrigger>
            )}
            {user.role === "owner" && (
              <TabsTrigger data-testid="tab-team" value="team" className="rounded-none -mb-px gap-2 border-b-2 border-transparent text-cappuccino data-[state=active]:bg-transparent data-[state=active]:border-gold data-[state=active]:text-ink data-[state=active]:shadow-none px-4 py-2.5">
                <Users className="w-4 h-4" /> Equipe
              </TabsTrigger>
            )}
            {canManage && (
              <TabsTrigger data-testid="tab-settings" value="settings" className="rounded-none -mb-px gap-2 border-b-2 border-transparent text-cappuccino data-[state=active]:bg-transparent data-[state=active]:border-gold data-[state=active]:text-ink data-[state=active]:shadow-none px-4 py-2.5">
                <SettingsIcon className="w-4 h-4" /> Configurações
              </TabsTrigger>
            )}
            {user.role === "owner" && (
              <TabsTrigger data-testid="tab-assinatura" value="assinatura" className="rounded-none -mb-px gap-2 border-b-2 border-transparent text-cappuccino data-[state=active]:bg-transparent data-[state=active]:border-gold data-[state=active]:text-ink data-[state=active]:shadow-none px-4 py-2.5">
                <Sparkles className="w-4 h-4" /> Assinatura
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="queue" className="mt-6">
            <QueueTab queue={queue} rooms={rooms} canManage={canManage} reload={loadQueue} limits={subscription?.limits} />
          </TabsContent>
          {canManage && subscription && (
            <TabsContent value="reports" className="mt-6">
              {subscription.limits?.reports ? <ReportsTab /> : (
                <div className="max-w-2xl bg-white border border-border rounded-md p-6 space-y-4" data-testid="reports-locked">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-6 h-6 text-gold" />
                    <h3 className="font-heading text-xl font-bold text-ink">Relatórios de atendimento</h3>
                  </div>
                  <p className="text-sm text-cappuccino">Veja tempo médio de espera, tempo de atendimento, horários de pico e atendimentos por sala.</p>
                  <UpgradeCTA plan="Profissional" text="Relatórios não estão incluídos no seu plano atual." testid="reports-upsell" />
                </div>
              )}
            </TabsContent>
          )}
          {canManage && (
            <TabsContent value="rooms" className="mt-6">
              <RoomsTab rooms={rooms} reload={loadRooms} />
            </TabsContent>
          )}
          {user.role === "owner" && (
            <TabsContent value="team" className="mt-6">
              <TeamTab currentUserId={user.id} />
            </TabsContent>
          )}
          {canManage && (
            <TabsContent value="settings" className="mt-6">
              <SettingsTab clinic={clinic} reload={loadClinic} tvUrl={tvUrl} limits={subscription?.limits} />
            </TabsContent>
          )}
          {user.role === "owner" && (
            <TabsContent value="assinatura" className="mt-6">
              <SubscriptionTab subscription={subscription} reload={loadSubscription} />
            </TabsContent>
          )}
        </Tabs>
      </main>

      <BlockedModal open={blocked} isOwner={user.role === "owner"} onSubscribe={() => { setBlocked(false); setTab("assinatura"); }} />
    </div>
  );
}

function BlockedModal({ open, isOwner, onSubscribe }) {
  return (
    <Dialog open={open}>
      <DialogContent className="max-w-md [&>button]:hidden" data-testid="blocked-modal">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-2">
            <Sparkles className="w-7 h-7 text-gold" />
          </div>
          <DialogTitle className="font-heading text-center text-2xl">Seu teste grátis terminou</DialogTitle>
          <DialogDescription className="text-center">
            {isOwner
              ? "Para continuar chamando pacientes, escolha um plano e ative sua assinatura."
              : "O período de teste da clínica terminou. Peça ao responsável para assinar um plano."}
          </DialogDescription>
        </DialogHeader>
        {isOwner && (
          <div>
            <Button data-testid="blocked-subscribe-button" onClick={onSubscribe}
              className="rounded-md bg-gold hover:bg-gold-hover text-white h-12 w-full">
              Ver planos e assinar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Queue ---------------- */
function QueueTab({ queue, rooms, canManage, reload, limits }) {
  const [name, setName] = useState("");
  const [priority, setPriority] = useState(false);
  const [targetRoom, setTargetRoom] = useState("none");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/queue", {
        name, priority,
        target_room_id: targetRoom === "none" ? null : targetRoom,
      });
      setName(""); setPriority(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const matchesFilter = (p) => {
    if (filter === "all") return true;
    if (filter === "geral") return !p.target_room_id;
    return p.target_room_id === filter;
  };

  const waiting = queue.waiting.filter(matchesFilter);
  const called = queue.called.filter((p) => filter === "all" || p.room_id === filter);
  const groups = groupWaiting(waiting);

  const waitingIds = waiting.map((p) => p.id);
  const allWaitingSelected = waitingIds.length > 0 && waitingIds.every((id) => selectedIds.has(id));
  const toggleWaiting = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllWaiting = () => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (allWaitingSelected) waitingIds.forEach((id) => n.delete(id));
    else waitingIds.forEach((id) => n.add(id));
    return n;
  });
  const bulkExclude = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await api.post("/queue/bulk-delete", { ids });
      setSelectedIds(new Set());
      reload();
      toast.success(`${ids.length} removido(s) da espera`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      {rooms.length > 0 && (
        <div className="flex items-center gap-2 mb-5">
          <span className="text-sm text-cappuccino">Filtrar por sala:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="queue-room-filter" className="w-52 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as salas</SelectItem>
              <SelectItem value="geral">Fila geral</SelectItem>
              {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_1.15fr] gap-6">
        {/* Waiting */}
        <section>
          {canManage && (
            <form onSubmit={add} className="bg-white border border-border rounded-xl p-5 mb-5 flex flex-col gap-3" data-testid="add-patient-form">
              <Label className="font-heading text-ink">Adicionar à fila</Label>
              <div className="flex gap-2">
                <Input data-testid="patient-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Nome do paciente" className="flex-1" />
                <Button type="submit" data-testid="add-patient-button" disabled={busy}
                  className="rounded-lg bg-gold hover:bg-gold-hover text-white gap-1">
                  <Plus className="w-4 h-4" /> Adicionar
                </Button>
                <IncludeListDialog rooms={rooms} reload={reload} limits={limits} />
              </div>
              {rooms.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-cappuccino whitespace-nowrap">Fila da sala:</span>
                  <Select value={targetRoom} onValueChange={setTargetRoom}>
                    <SelectTrigger data-testid="target-room-select" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Fila geral (qualquer sala)</SelectItem>
                      {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-cappuccino cursor-pointer">
                <Checkbox data-testid="priority-checkbox" checked={priority} onCheckedChange={(v) => setPriority(!!v)} />
                Atendimento prioritário (idoso, gestante, PcD)
              </label>
            </form>
          )}

          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-heading font-bold text-ink flex items-center gap-2">
              Aguardando <Badge variant="secondary" className="bg-accent text-cappuccino">{waiting.length}</Badge>
            </h3>
            {canManage && waiting.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-cappuccino cursor-pointer">
                  <Checkbox data-testid="waiting-select-all" checked={allWaitingSelected} onCheckedChange={toggleAllWaiting} />
                  Selecionar todos
                </label>
                {selectedIds.size > 0 && (
                  <Button data-testid="bulk-exclude-button" variant="outline" size="sm"
                    className="rounded-md gap-1 border-destructive text-destructive hover:bg-destructive hover:text-white"
                    onClick={bulkExclude}>
                    <Trash2 className="w-4 h-4" /> Excluir ({selectedIds.size})
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="space-y-5" data-testid="waiting-list">
            {waiting.length === 0 && <EmptyNote text="Ninguém aguardando." />}
            {groups.map((grp) => (
              <div key={grp.key}>
                {grp.label && (
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-cappuccino mb-2 flex items-center gap-1.5">
                    <DoorOpen className="w-3.5 h-3.5 text-gold" /> {grp.label}
                  </p>
                )}
                <div className="space-y-3">
                  {grp.items.map((p) => (
                    <WaitingRow key={p.id} p={p} rooms={rooms} canManage={canManage} reload={reload}
                      selected={selectedIds.has(p.id)} onToggle={() => toggleWaiting(p.id)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Called */}
        <section>
          <h3 className="font-heading font-bold text-ink mb-3 flex items-center gap-2">
            Em atendimento <Badge variant="secondary" className="bg-accent text-cappuccino">{called.length}</Badge>
          </h3>
          <div className="space-y-3" data-testid="called-list">
            {called.length === 0 && <EmptyNote text="Nenhum paciente chamado ainda." />}
            {called.map((p) => (
              <div key={p.id} className="bg-white border border-border rounded-xl p-4 flex items-center gap-3 animate-fade-up">
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-ink truncate">{p.name}</p>
                  <p className="text-sm text-cappuccino flex items-center gap-1.5">
                    {p.room_name} · <Timer className="w-3.5 h-3.5" /> <WaitTimer since={p.called_at} prefix="há " />
                  </p>
                </div>
                <Button data-testid={`recall-${p.id}`} variant="outline" size="sm" className="rounded-md gap-1"
                  onClick={async () => { await api.post(`/queue/${p.id}/recall`); reload(); toast.success("Rechamando na TV"); }}>
                  <RotateCcw className="w-4 h-4" /> Rechamar
                </Button>
                <Button data-testid={`done-${p.id}`} size="sm" className="rounded-md gap-1 bg-ink hover:bg-ink/90 text-white"
                  onClick={async () => { await api.post(`/queue/${p.id}/done`); reload(); }}>
                  <Check className="w-4 h-4" /> Concluir
                </Button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function IncludeListDialog({ rooms, reload, limits }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("registered");
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [targetRoom, setTargetRoom] = useState("none");
  const [registerText, setRegisterText] = useState("");
  const [singleName, setSingleName] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/patients");
    setPatients(data);
  }, []);

  useEffect(() => {
    if (open) {
      load(); setSelected(new Set()); setSearch(""); setRegisterText(""); setSingleName(""); setTab("registered");
    }
  }, [open, load]);

  const roomId = targetRoom === "none" ? null : targetRoom;

  const parseNames = (text) =>
    text
      .split(/[\n\r]+/)
      .map((l) => l.replace(/^[\s;,\t•\-*\d.()]+/, "").split(/\t|;|,{2,}/)[0].trim())
      .filter(Boolean);

  // Register pasted list INTO THE SYSTEM (not the queue)
  const registerList = async () => {
    const names = parseNames(registerText);
    if (names.length === 0) { toast.error("Cole ao menos um nome (um por linha)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/patients/bulk", { names });
      await load();
      setRegisterText("");
      toast.success(`${data.added} cadastrado(s)${data.skipped ? `, ${data.skipped} já existiam` : ""}`);
      setTab("registered");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Import names from a .csv/.xlsx spreadsheet into the textarea (then register)
  const importSpreadsheet = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
      let names = rows
        .map((r) => (Array.isArray(r) ? r[0] : r))
        .map((v) => (v == null ? "" : String(v).trim()))
        .filter(Boolean);
      if (names.length && /^(nome|nomes|name|names|paciente|pacientes|cliente|clientes)$/i.test(names[0])) {
        names = names.slice(1);
      }
      if (names.length === 0) { toast.error("Nenhum nome encontrado na planilha."); return; }
      setRegisterText((prev) => (prev.trim() ? prev.trim() + "\n" : "") + names.join("\n"));
      toast.success(`${names.length} nome(s) importado(s) da planilha`);
    } catch (err) {
      toast.error("Não foi possível ler a planilha. Use .csv ou .xlsx.");
    }
  };

  // Launch a single walk-in directly into the queue
  const includeSingle = async () => {
    if (!singleName.trim()) return;
    try {
      await api.post("/queue", { name: singleName, target_room_id: roomId });
      setSingleName("");
      reload();
      toast.success("Paciente lançado na fila");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = patients.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) filtered.forEach((p) => n.delete(p.id));
    else filtered.forEach((p) => n.add(p.id));
    return n;
  });

  const addNewPatient = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/patients", { name: newName });
      setNewName("");
      setPatients((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Paciente cadastrado no sistema");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removePatient = async (id) => {
    await api.delete(`/patients/${id}`);
    setPatients((prev) => prev.filter((p) => p.id !== id));
  };

  // Launch selected registered patients INTO THE QUEUE
  const launchSelected = async () => {
    if (selected.size === 0) { toast.error("Selecione ao menos um paciente."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/queue/bulk", { patient_ids: Array.from(selected), target_room_id: roomId });
      reload(); setOpen(false);
      toast.success(`${data.added} paciente(s) lançado(s) na fila`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" data-testid="include-list-button" variant="outline"
          className="rounded-lg gap-1 border-gold text-ink hover:bg-accent">
          <ListPlus className="w-4 h-4" /> Incluir
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Pacientes</DialogTitle>
          <DialogDescription>Cadastre pacientes no sistema e lance-os na fila quando chegarem.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-accent rounded-md p-1 h-auto w-full">
            <TabsTrigger value="registered" className="rounded-md flex-1 gap-1.5 data-[state=active]:bg-white"><Users className="w-4 h-4" /> Lançar na fila</TabsTrigger>
            <TabsTrigger value="register" className="rounded-md flex-1 gap-1.5 data-[state=active]:bg-white"><ClipboardPaste className="w-4 h-4" /> Cadastrar lista</TabsTrigger>
            <TabsTrigger value="single" className="rounded-md flex-1 gap-1.5 data-[state=active]:bg-white"><Plus className="w-4 h-4" /> Avulso</TabsTrigger>
          </TabsList>

          {/* Launch registered patients into queue */}
          <TabsContent value="registered" className="mt-4 space-y-3">
            {rooms.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-cappuccino whitespace-nowrap">Fila da sala:</span>
                <Select value={targetRoom} onValueChange={setTargetRoom}>
                  <SelectTrigger data-testid="bulk-target-room" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Fila geral (qualquer sala)</SelectItem>
                    {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cappuccino" />
              <Input data-testid="patient-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar paciente cadastrado…" className="pl-9" />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-cappuccino cursor-pointer">
                <Checkbox data-testid="select-all-patients" checked={allSelected} onCheckedChange={toggleAll} />
                Selecionar todos ({filtered.length})
              </label>
              <span className="text-sm font-medium text-gold">{selected.size} selecionado(s)</span>
            </div>
            <div className="max-h-44 overflow-y-auto border border-border rounded-xl divide-y divide-border" data-testid="patients-list">
              {filtered.length === 0 && <p className="text-sm text-cappuccino/70 p-4 text-center">Nenhum paciente cadastrado. Use "Cadastrar lista".</p>}
              {filtered.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/50">
                  <Checkbox data-testid={`patient-check-${p.id}`} checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="flex-1 text-sm font-medium text-ink truncate">{p.name}</span>
                  <button onClick={() => removePatient(p.id)} className="text-cappuccino hover:text-destructive" data-testid={`patient-remove-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <form onSubmit={addNewPatient} className="flex gap-2">
              <Input data-testid="new-patient-input" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Cadastrar novo paciente" className="flex-1" />
              <Button type="submit" variant="outline" data-testid="patient-register-button" className="rounded-lg gap-1"><Plus className="w-4 h-4" /> Cadastrar</Button>
            </form>
            <Button data-testid="bulk-add-button" onClick={launchSelected} disabled={busy}
              className="rounded-md bg-gold hover:bg-gold-hover text-white gap-1 w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListPlus className="w-4 h-4" />}
              Lançar {selected.size > 0 ? `${selected.size} ` : ""}na fila
            </Button>
          </TabsContent>

          {/* Register a pasted list into the SYSTEM */}
          <TabsContent value="register" className="mt-4 space-y-3">
            <p className="text-xs text-cappuccino">Cole a lista que a clínica já tem (Excel/outro sistema), um nome por linha, ou importe uma planilha. Eles são <strong>cadastrados no sistema</strong> — não entram na fila agora.</p>
            {limits?.bulk_import ? (
              <label className="cursor-pointer inline-block">
                <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-spreadsheet-input" onChange={importSpreadsheet} />
                <span className="inline-flex items-center gap-2 rounded-md border border-gold text-ink px-4 py-2 text-sm font-medium hover:bg-accent transition-colors">
                  <FileSpreadsheet className="w-4 h-4 text-gold" /> Importar planilha (.csv/.xlsx)
                </span>
              </label>
            ) : (
              <UpgradeCTA plan="Profissional" testid="bulk-import-upsell"
                text="Importar planilhas e cadastrar listas em massa não está no seu plano. Você ainda pode cadastrar pacientes um a um." />
            )}
            <Textarea data-testid="register-list-input" value={registerText} onChange={(e) => setRegisterText(e.target.value)}
              rows={6} placeholder={"Maria Silva\nJoão Souza\nAna Oliveira"} />
            <p className="text-xs text-cappuccino">{parseNames(registerText).length} nome(s) detectado(s).</p>
            <Button data-testid="register-list-button" onClick={registerList} disabled={busy || !limits?.bulk_import}
              className="rounded-md bg-ink hover:bg-ink/90 text-white gap-1 w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPaste className="w-4 h-4" />} Cadastrar lista no sistema
            </Button>
          </TabsContent>

          {/* Walk-in single, straight to queue */}
          <TabsContent value="single" className="mt-4 space-y-3">
            {rooms.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-cappuccino whitespace-nowrap">Fila da sala:</span>
                <Select value={targetRoom} onValueChange={setTargetRoom}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Fila geral (qualquer sala)</SelectItem>
                    {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <Input data-testid="single-name-input" value={singleName} onChange={(e) => setSingleName(e.target.value)}
                placeholder="Nome do paciente (encaixe/avulso)" className="flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); includeSingle(); } }} />
              <Button data-testid="include-single-button" onClick={includeSingle}
                className="rounded-lg bg-gold hover:bg-gold-hover text-white gap-1"><Plus className="w-4 h-4" /> Na fila</Button>
            </div>
            <p className="text-xs text-cappuccino">Para encaixes/avulsos: lança direto na fila (e também fica cadastrado).</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function groupWaiting(items) {
  const hasRooms = items.some((p) => p.target_room_name);
  if (!hasRooms) return [{ key: "all", label: null, items }];
  const map = new Map();
  for (const p of items) {
    const key = p.target_room_id || "geral";
    const label = p.target_room_name || "Fila geral";
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(p);
  }
  return Array.from(map.values());
}

function WaitingRow({ p, rooms, canManage, reload, selected, onToggle }) {
  const hasTarget = !!p.target_room_id;
  const [roomId, setRoomId] = useState(p.target_room_id || rooms[0]?.id || "");
  useEffect(() => { if (!roomId && rooms[0]) setRoomId(rooms[0].id); }, [rooms, roomId]);

  const call = async (rid) => {
    const useRoom = rid || roomId;
    if (!useRoom) { toast.error("Cadastre uma sala primeiro."); return; }
    try {
      await api.post(`/queue/${p.id}/call`, { room_id: useRoom });
      reload();
      toast.success(`${p.name} chamado(a)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className={`bg-white border rounded-xl p-4 flex items-center gap-3 animate-fade-up transition-colors ${selected ? "border-gold bg-accent/40" : "border-border"}`}>
      {canManage && (
        <Checkbox data-testid={`waiting-check-${p.id}`} checked={selected} onCheckedChange={onToggle} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-heading font-semibold text-ink truncate">{p.name}</p>
          {p.priority && <Badge className="bg-gold text-ink hover:bg-gold">Prioridade</Badge>}
        </div>
        <p className="text-xs text-cappuccino flex items-center gap-1 mt-0.5" data-testid={`wait-${p.id}`}>
          <Hourglass className="w-3 h-3" /> Esperando há <WaitTimer since={p.created_at} />
        </p>
      </div>
      {!hasTarget && (
        <Select value={roomId} onValueChange={setRoomId}>
          <SelectTrigger data-testid={`room-select-${p.id}`} className="w-36 h-9"><SelectValue placeholder="Sala" /></SelectTrigger>
          <SelectContent>
            {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button data-testid={`call-${p.id}`} onClick={() => call(p.target_room_id)} className="rounded-md gap-1 bg-gold hover:bg-gold-hover text-white">
        <Megaphone className="w-4 h-4" /> Chamar
      </Button>
      {canManage && (
        <Button data-testid={`remove-${p.id}`} variant="ghost" size="icon" className="text-cappuccino hover:text-destructive"
          onClick={async () => { await api.delete(`/queue/${p.id}`); reload(); }}>
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

/* ---------------- Rooms ---------------- */
function RoomsTab({ rooms, reload }) {
  const [name, setName] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/rooms", { name });
      setName(""); reload();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "limit_rooms") toast.error("Limite de salas do seu plano atingido. Faça upgrade na aba Assinatura.");
      else if (d !== "subscription_required") toast.error(formatApiErrorDetail(d));
    }
  };
  return (
    <div className="max-w-xl">
      <form onSubmit={add} className="flex gap-2 mb-5" data-testid="add-room-form">
        <Input data-testid="room-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Consultório 3" />
        <Button type="submit" data-testid="add-room-button" className="rounded-lg bg-gold hover:bg-gold-hover text-white gap-1">
          <Plus className="w-4 h-4" /> Criar sala
        </Button>
      </form>
      <div className="space-y-2">
        {rooms.length === 0 && <EmptyNote text="Nenhuma sala cadastrada." />}
        {rooms.map((r) => (
          <div key={r.id} className="bg-white border border-border rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="flex items-center gap-2 font-medium text-ink"><DoorOpen className="w-4 h-4 text-gold" /> {r.name}</span>
            <Button data-testid={`delete-room-${r.id}`} variant="ghost" size="icon" className="text-cappuccino hover:text-destructive"
              onClick={async () => { await api.delete(`/rooms/${r.id}`); reload(); }}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Team ---------------- */
function TeamTab({ currentUserId }) {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "reception" });
  const load = useCallback(async () => {
    const { data } = await api.get("/team"); setMembers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team", form);
      setForm({ name: "", email: "", password: "", role: "reception" });
      load();
      toast.success("Membro adicionado");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "limit_team") toast.error("Limite de usuários do seu plano atingido. Faça upgrade na aba Assinatura.");
      else if (d !== "subscription_required") toast.error(formatApiErrorDetail(d));
    }
  };

  return (
    <div className="grid md:grid-cols-[1fr_1fr] gap-6 max-w-4xl">
      <form onSubmit={add} className="bg-white border border-border rounded-xl p-5 space-y-3 h-fit" data-testid="add-member-form">
        <Label className="font-heading text-ink flex items-center gap-2"><UserPlus className="w-4 h-4 text-gold" /> Convidar membro</Label>
        <Input data-testid="member-name-input" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Input data-testid="member-email-input" type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <Input data-testid="member-password-input" type="text" placeholder="Senha inicial" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="reception">Recepção</SelectItem>
            <SelectItem value="professional">Profissional</SelectItem>
            <SelectItem value="owner">Dono</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" data-testid="add-member-button" className="w-full rounded-lg bg-gold hover:bg-gold-hover text-white">Adicionar</Button>
      </form>

      <div className="space-y-2" data-testid="team-list">
        {members.map((m) => (
          <div key={m.id} className="bg-white border border-border rounded-xl px-4 py-3 flex items-center justify-between">
            <div>
              <p className="font-medium text-ink">{m.name}</p>
              <p className="text-sm text-cappuccino">{m.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-accent text-cappuccino">{ROLE_LABELS[m.role]}</Badge>
              {m.id !== currentUserId && (
                <Button data-testid={`delete-member-${m.id}`} variant="ghost" size="icon" className="text-cappuccino hover:text-destructive"
                  onClick={async () => { await api.delete(`/team/${m.id}`); load(); }}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
const MEDIA_BASE = API.replace(/\/api$/, "");
const toAbs = (u) => (u && u.startsWith("http") ? u : `${MEDIA_BASE}${u}`);

function SettingsTab({ clinic, reload, tvUrl, limits }) {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    if (clinic) {
      const media = (clinic.media_items || []).map((m) => {
        const it = typeof m === "string" ? { type: "image", url: m } : { ...m };
        if (it.type !== "video" && !parseYouTube(it.url) && (it.duration == null || it.duration === "")) {
          it.duration = it.type === "embed" ? 30 : 8;
        }
        return it;
      });
      if (clinic.video_url) media.push({ type: "embed", url: clinic.video_url });
      setForm({
        name: clinic.name || "",
        primary_color: clinic.primary_color || "#C5A059",
        logo_url: clinic.logo_url || "",
        display_mode: clinic.display_mode || "name_room",
        welcome_message: clinic.welcome_message || "",
        media,
        speech_enabled: clinic.speech_enabled !== false,
        tts_voice: clinic.tts_voice || "female",
        tts_speed: clinic.tts_speed || "normal",
        tts_language: clinic.tts_language || "pt-BR",
        embedInput: "",
      });
    }
  }, [clinic]);

  if (!form) return null;

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data; // {id, type, url}
  };

  const onMediaPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    let done = 0;
    for (const [i, f] of files.entries()) {
      if (!limits?.video && !(f.type || "").startsWith("image/")) {
        toast.error(`${f.name}: vídeos na TV estão disponíveis a partir do plano Profissional.`);
        continue;
      }
      setUploading({ name: f.name, pct: 0, n: i + 1, total: files.length });
      try {
        const res = await uploadMediaChunked(f, (pct) => setUploading((u) => (u ? { ...u, pct } : u)));
        setForm((prev) => ({ ...prev, media: [...prev.media, res] }));
        done += 1;
      } catch (err) {
        toast.error(`${f.name}: ${err.friendly ? err.message : formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    setUploading(null);
    if (done) toast.success(`${done} arquivo(s) enviado(s). Clique em "Salvar configurações" para publicar na TV.`);
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    try {
      const data = await uploadFile(file);
      setForm((prev) => ({ ...prev, logo_url: toAbs(data.url) }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLogoUploading(false); }
  };

  const save = async () => {
    try {
      await api.put("/clinic", {
        name: form.name,
        primary_color: form.primary_color,
        logo_url: form.logo_url,
        display_mode: form.display_mode,
        welcome_message: form.welcome_message,
        idle_panel: "media",
        media_items: form.media.filter((m) => limits?.video || m.type === "image").map((m) => ({
          ...(m.id ? { id: m.id } : {}),
          type: m.type,
          url: m.url,
          ...(m.type !== "video" && !parseYouTube(m.url)
            ? { duration: Number(m.duration) || (m.type === "embed" ? 30 : 8) }
            : {}),
        })),
        video_url: "",
        speech_enabled: form.speech_enabled,
        tts_voice: form.tts_voice,
        tts_speed: form.tts_speed,
        tts_language: form.tts_language,
      });
      reload();
      toast.success("Configurações salvas");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setDuration = (idx, val) => setForm((prev) => ({
    ...prev,
    media: prev.media.map((m, i) => (i === idx ? { ...m, duration: val } : m)),
  }));

  const addEmbed = () => {
    const url = (form.embedInput || "").trim();
    if (!url) return;
    setForm((prev) => ({
      ...prev,
      media: [...prev.media, parseYouTube(url) ? { type: "embed", url } : { type: "embed", url, duration: 30 }],
      embedInput: "",
    }));
  };

  return (
    <div className="max-w-2xl space-y-6">
      <TvCodeCard />
      <div className="bg-white border border-border rounded-xl p-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="font-heading font-semibold text-ink">Link do painel da TV (computador, TV Box, Android TV)</p>
          <p className="text-sm text-cappuccino truncate">{tvUrl}</p>
        </div>
        <Button data-testid="copy-tv-link" variant="outline" className="rounded-md gap-2 shrink-0"
          onClick={() => { navigator.clipboard.writeText(tvUrl); toast.success("Link copiado"); }}>
          <Copy className="w-4 h-4" /> Copiar
        </Button>
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-5">
        <div>
          <Label>Nome da clínica</Label>
          <Input data-testid="setting-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>Mensagem de boas-vindas na TV</Label>
          <textarea data-testid="setting-welcome" value={form.welcome_message}
            onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
            rows={2} placeholder="Ex: Seja bem-vindo à Renova Prime! Em breve você será chamado."
            className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-ink outline-none focus:ring-2 focus:ring-gold resize-none" />
          <p className="text-xs text-cappuccino mt-1">Aparece na TV enquanto ninguém está sendo chamado.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Cor da marca</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" data-testid="setting-color" value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="w-12 h-10 rounded border border-border cursor-pointer" />
              <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Logo da clínica</Label>
            <div className="flex items-center gap-3 mt-1.5">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="h-10 w-10 rounded-lg object-contain border border-border bg-cream" />
              ) : (
                <div className="h-10 w-10 rounded-lg border border-dashed border-border flex items-center justify-center text-cappuccino">
                  <ImageIcon className="w-4 h-4" />
                </div>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={onLogoPick} />
                <span className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent transition-colors">
                  {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Enviar logo
                </span>
              </label>
              {form.logo_url && (
                <Button variant="ghost" size="sm" className="text-cappuccino hover:text-destructive" data-testid="remove-logo"
                  onClick={() => setForm({ ...form, logo_url: "" })}>Remover</Button>
              )}
            </div>
          </div>
        </div>
        <div>
          <Label>O que a TV mostra ao chamar</Label>
          <Select value={form.display_mode} onValueChange={(v) => setForm({ ...form, display_mode: v })}>
            <SelectTrigger data-testid="setting-display-mode" className="mt-1.5 sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name_room">Nome + Sala</SelectItem>
              <SelectItem value="name_only">Apenas o nome</SelectItem>
              <SelectItem value="ticket_room">Senha + Sala</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-cappuccino mt-1.5">A tela do chamador sempre mostra a chamada atual, a lista de <strong>Próximos</strong> e os <strong>Já chamados</strong>. O painel lateral exibe suas {limits?.video ? "imagens e vídeos" : "imagens"}.</p>
        </div>

        <div className="space-y-4 border-t border-border pt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <Label className="mb-0">Mídia do painel lateral da TV</Label>
              <p className="text-xs text-cappuccino">{limits?.video ? "Imagens, vídeos, YouTube ou TV ao vivo que passam na lateral." : "Imagens que passam na lateral da TV."}</p>
            </div>
            {limits?.media && (
              <label className={uploading ? "pointer-events-none opacity-60" : "cursor-pointer"}>
                <input type="file" accept={limits?.video ? "image/*,video/*" : "image/*"} multiple className="hidden" data-testid="media-upload-input" onChange={onMediaPick} disabled={!!uploading} />
                <span className="inline-flex items-center gap-2 rounded-md bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm font-medium transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {limits?.video ? "Enviar imagem/vídeo" : "Enviar imagem"}
                </span>
              </label>
            )}
          </div>
          {limits?.media && !limits?.video && <p className="text-xs text-cappuccino -mt-2">Imagens até 30 MB (jpg, png, webp).</p>}
          {limits?.video && <p className="text-xs text-cappuccino -mt-2">Vídeos até <strong>1 GB</strong> (mp4, webm, mov) e imagens até 30 MB. Arquivos grandes são enviados em partes. Vídeos e YouTube sempre tocam até o fim e depois passam para o próximo; na TV, o botão <strong>Próxima</strong> (ou a seta → do controle) pula a mídia atual.</p>}
          {uploading && (
            <div className="rounded-md border border-border bg-cream px-3 py-2.5" data-testid="upload-progress">
              <div className="flex items-center justify-between text-xs text-ink mb-1.5">
                <span className="truncate">Enviando {uploading.total > 1 ? `(${uploading.n}/${uploading.total}) ` : ""}{uploading.name}</span>
                <span className="font-semibold tabular-nums" data-testid="upload-progress-pct">{uploading.pct}%</span>
              </div>
              <div className="h-1.5 bg-cappuccino/15 overflow-hidden">
                <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${uploading.pct}%` }} />
              </div>
              <p className="text-[0.7rem] text-cappuccino mt-1.5">Não feche esta página até terminar.</p>
            </div>
          )}

          {!limits?.media ? (
            <UpgradeCTA plan="Essencial" testid="media-upsell"
              text="Imagens, vídeos e YouTube no painel lateral da TV não estão no seu plano." />
          ) : !limits?.video ? (
            <UpgradeCTA plan="Profissional" testid="video-upsell"
              text="Vídeos, YouTube e TV ao vivo no painel da TV não estão no seu plano. Seu plano exibe imagens." />
          ) : (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Youtube className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cappuccino" />
              <Input data-testid="embed-input" value={form.embedInput}
                onChange={(e) => setForm({ ...form, embedInput: e.target.value })}
                placeholder="Link YouTube/TV ao vivo (embed)…" className="pl-9"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmbed(); } }} />
            </div>
            <Button type="button" data-testid="add-embed-button" variant="outline" className="rounded-lg gap-1" onClick={addEmbed}>
              <Plus className="w-4 h-4" /> Adicionar link
            </Button>
          </div>
          )}

          {form.media.length === 0 ? (
            <EmptyNote text={limits?.video ? "Nenhuma mídia. Suba fotos/vídeos ou adicione um link de YouTube/TV ao vivo para passar entre as chamadas." : "Nenhuma mídia. Suba imagens para passar entre as chamadas."} />
          ) : (
            <div className="space-y-2" data-testid="media-grid">
              {form.media.map((m, idx) => (
                <div key={m.id || m.url + idx} className="flex items-center gap-3 bg-cream border border-border rounded-xl p-2.5">
                  <div className="w-16 h-12 rounded-lg overflow-hidden bg-ink/5 shrink-0 flex items-center justify-center">
                    {m.type === "video" ? (
                      <video src={toAbs(m.url)} className="w-full h-full object-cover" muted />
                    ) : m.type === "embed" ? (
                      <Youtube className="w-5 h-5 text-cappuccino" />
                    ) : (
                      <img src={toAbs(m.url)} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink flex items-center gap-1.5">
                      {m.type === "video" ? <><Film className="w-3.5 h-3.5" /> Vídeo</> : m.type === "embed" ? <><Youtube className="w-3.5 h-3.5" /> Link / TV ao vivo</> : <><ImageIcon className="w-3.5 h-3.5" /> Imagem</>}
                    </p>
                    {m.type === "embed" && <p className="text-xs text-cappuccino truncate">{m.url}</p>}
                    {!limits?.video && m.type !== "image" && (
                      <p className="text-xs text-red-700" data-testid={`media-locked-${idx}`}>Não aparece na TV no seu plano — será removido ao salvar.</p>
                    )}
                  </div>
                  {m.type === "video" || (m.type === "embed" && parseYouTube(m.url)) ? (
                    <span className="text-xs text-cappuccino shrink-0" data-testid={`media-until-end-${idx}`}>Toca até o fim</span>
                  ) : (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input type="number" min="1" data-testid={`media-duration-${idx}`} value={m.duration}
                        onChange={(e) => setDuration(idx, e.target.value)} className="w-16 h-8 text-center" />
                      <span className="text-xs text-cappuccino">seg</span>
                    </div>
                  )}
                  <button data-testid={`remove-media-${idx}`}
                    onClick={() => setForm({ ...form, media: form.media.filter((_, i) => i !== idx) })}
                    className="text-cappuccino hover:text-destructive shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="flex items-center justify-between border-t border-border pt-4">
          <span className="font-medium text-ink">Chamada por voz na TV</span>
          <Switch data-testid="setting-speech" checked={form.speech_enabled} onCheckedChange={(v) => setForm({ ...form, speech_enabled: v })} />
        </label>
        <VoiceSettings clinicId={clinic.id} form={form} setForm={setForm} />

        <Button data-testid="save-settings" onClick={save} className="rounded-md bg-gold hover:bg-gold-hover text-white">
          Salvar configurações
        </Button>
      </div>
    </div>
  );
}

function ReportsTab() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/reports");
    setData(data);
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const cards = [
    { label: "Atendidos hoje", value: data.today_attended, icon: Check, testid: "report-attended" },
    { label: "Na fila agora", value: data.waiting_now, icon: Hourglass, testid: "report-waiting" },
    { label: "Tempo médio de espera", value: `${data.avg_wait_min} min`, icon: Clock, testid: "report-avg-wait" },
    { label: "Tempo médio de atendimento", value: `${data.avg_service_min} min`, icon: Timer, testid: "report-avg-service" },
  ];
  const maxHour = Math.max(1, ...data.by_hour.map((h) => h.count));
  const fmtHour = (h) => `${String(h).padStart(2, "0")}h`;

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-cappuccino">{c.label}</span>
              <c.icon className="w-4 h-4 text-gold" />
            </div>
            <p className="font-heading text-3xl font-bold text-ink">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-bold text-ink">Horários de pico (entradas)</h3>
            {data.peak_hour != null && (
              <Badge className="bg-gold text-ink hover:bg-gold">Pico: {fmtHour(data.peak_hour)}</Badge>
            )}
          </div>
          <div className="flex items-end gap-1 h-44" data-testid="peak-hours-chart">
            {data.by_hour.filter((h) => h.hour >= 6 && h.hour <= 21).map((h) => (
              <div key={h.hour} className="flex-1 flex flex-col items-center justify-end gap-1 group">
                <span className="text-[0.6rem] text-cappuccino opacity-0 group-hover:opacity-100 transition-opacity">{h.count}</span>
                <div className="w-full rounded-t-md transition-all"
                  style={{ height: `${(h.count / maxHour) * 100}%`, minHeight: h.count > 0 ? 4 : 0, background: "#C5A059" }} />
                <span className="text-[0.55rem] text-cappuccino">{h.hour}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-cappuccino mt-2 text-center">Hora do dia (06h–21h) · todas as entradas registradas</p>
        </div>

        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading font-bold text-ink mb-5">Atendimentos por sala (30 dias)</h3>
          <div className="space-y-3" data-testid="by-room-list">
            {data.by_room.length === 0 && <p className="text-sm text-cappuccino/70">Sem dados ainda.</p>}
            {data.by_room.map((r) => {
              const max = Math.max(1, ...data.by_room.map((x) => x.count));
              return (
                <div key={r.room_name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-ink flex items-center gap-1.5"><DoorOpen className="w-3.5 h-3.5 text-gold" /> {r.room_name}</span>
                    <span className="text-cappuccino">{r.count}</span>
                  </div>
                  <div className="h-2 rounded-md bg-accent overflow-hidden">
                    <div className="h-full rounded-md" style={{ width: `${(r.count / max) * 100}%`, background: "#8B6D5C" }} />
                  </div>
                </div>
              );
            })}
          </div>
          {data.longest_wait_min > 0 && (
            <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
              <span className="text-sm text-cappuccino">Maior espera atual</span>
              <span className="font-heading font-bold text-ink">{data.longest_wait_min} min</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubscriptionTab({ subscription, reload }) {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [loadingKey, setLoadingKey] = useState(null);
  const [partnerCode, setPartnerCode] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [freeLoading, setFreeLoading] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  const brl = (v) => {
    const n = Number(v);
    return `R$ ${n.toLocaleString("pt-BR", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  };
  const subscribe = async (plan) => {
    const lk = billing === "yearly" ? plan.yearly.lookup_key : plan.monthly.lookup_key;
    setLoadingKey(lk);
    try {
      const { data } = await api.post("/subscription/checkout", { lookup_key: lk, origin_url: window.location.origin, partner_code: partnerCode || undefined });
      window.location.href = data.checkout_url;
    } catch (err) {
      setLoadingKey(null);
      const d = err.response?.data?.detail;
      toast.error(d === "partner_invalid" ? "Código de parceiro inválido." : formatApiErrorDetail(d));
    }
  };
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setPortalLoading(false);
      toast.error("Não foi possível abrir o portal de cobrança.");
    }
  };
  const activateFree = async () => {
    setFreeLoading(true);
    try {
      await api.post("/subscription/activate-free");
      toast.success("Plano Grátis ativado.");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setFreeLoading(false);
    }
  };

  const active = subscription?.subscription_status === "active";
  const statusLabel = active ? "Ativa" : subscription?.subscription_status === "trialing" ? "Teste grátis" : "Expirada";

  return (
    <div className="space-y-6" data-testid="subscription-tab">
      {/* Current status */}
      <div className="bg-white border border-border rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-cappuccino">Plano atual</p>
          <div className="flex items-center gap-2 mt-1">
            <Crown className="w-5 h-5 text-gold" />
            <span className="font-heading text-2xl font-bold text-ink">{subscription?.plan_name || "—"}</span>
            <Badge className={active ? "bg-green-600 text-white hover:bg-green-600" : "bg-gold text-ink hover:bg-gold"}>{statusLabel}</Badge>
          </div>
          {subscription?.subscription_status === "trialing" && subscription?.trial_days_left != null && (
            <p className="text-sm text-cappuccino mt-1">{subscription.trial_days_left} dia(s) restante(s) de teste</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-3">
          {subscription?.usage && subscription?.limits && (
            <div className="flex gap-6 text-sm">
              <div><span className="text-cappuccino">Salas</span><p className="font-heading font-bold text-ink text-lg">{subscription.usage.rooms}{subscription.limits.rooms === -1 ? "" : `/${subscription.limits.rooms}`}</p></div>
              <div><span className="text-cappuccino">TVs ativas</span><p className="font-heading font-bold text-ink text-lg" data-testid="sub-usage-tvs">{subscription.usage.tvs ?? 0}{subscription.limits.tvs === -1 ? "" : `/${subscription.limits.tvs}`}</p></div>
              <div><span className="text-cappuccino">Usuários</span><p className="font-heading font-bold text-ink text-lg">{subscription.usage.team}{subscription.limits.team === -1 ? "" : `/${subscription.limits.team}`}</p></div>
            </div>
          )}
          {subscription?.has_billing_portal && (
            <Button onClick={openPortal} disabled={portalLoading} variant="outline"
              data-testid="sub-portal-btn" className="rounded-md h-10 border-ink/20 gap-2">
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 text-gold" />}
              Gerenciar cobrança
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center">
        <div className="inline-flex items-center gap-1 bg-accent rounded-md p-1">
          <button onClick={() => setBilling("monthly")} data-testid="sub-billing-monthly"
            className={`px-5 py-2 rounded-md text-sm font-semibold ${billing === "monthly" ? "bg-white text-ink shadow" : "text-cappuccino"}`}>Mensal</button>
          <button onClick={() => setBilling("yearly")} data-testid="sub-billing-yearly"
            className={`px-5 py-2 rounded-md text-sm font-semibold flex items-center gap-2 ${billing === "yearly" ? "bg-white text-ink shadow" : "text-cappuccino"}`}>
            Anual <span className="text-[0.65rem] font-bold bg-gold text-ink px-2 py-0.5 rounded-md">-27%</span>
          </button>
        </div>
      </div>

      <div className="flex justify-center -mt-1">
        <div className="w-full max-w-sm">
          <label className="block text-xs font-semibold text-cappuccino mb-1.5 text-center">Tem código de parceiro? (desconto do combo)</label>
          <input data-testid="sub-partner-code-input" value={partnerCode} onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
            placeholder="Ex: PARCEIRO"
            className="w-full rounded-md border border-border bg-white px-5 h-11 text-sm text-ink outline-none focus:ring-2 focus:ring-gold" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
        {plans.map((plan) => {
          const amount = billing === "yearly" ? plan.yearly.amount : plan.monthly.amount;
          const lk = billing === "yearly" ? plan.yearly.lookup_key : plan.monthly.lookup_key;
          const isCurrent = active && subscription?.plan === plan.id;
          const isFree = plan.free;
          const isContact = plan.contact;
          return (
            <div key={plan.id} data-testid={`sub-plan-${plan.id}`}
              className={`rounded-3xl p-6 flex flex-col ${plan.highlight ? "bg-ink text-cream ring-2 ring-gold" : "bg-white border border-border"}`}>
              <h3 className="font-heading text-xl font-bold">{plan.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-heading text-3xl font-extrabold">{isFree ? "Grátis" : isContact ? "Sob consulta" : brl(amount)}</span>
                {!isFree && !isContact && <span className={`mb-1 text-sm ${plan.highlight ? "text-cream/60" : "text-cappuccino"}`}>/{billing === "yearly" ? "ano" : "mês"}</span>}
              </div>
              {isContact && !isCurrent ? (
                <a href={plan.contact_url} target="_blank" rel="noopener noreferrer" data-testid={`sub-contact-${plan.id}`}
                  className="mt-5 rounded-md h-11 font-semibold gap-2 inline-flex items-center justify-center bg-[#1F7A4D] hover:bg-[#19633F] text-white text-sm transition-colors">
                  <MessageCircle className="w-4 h-4" /> Entrar em contato
                </a>
              ) : isFree ? (
                <Button onClick={activateFree} disabled={freeLoading || isCurrent}
                  data-testid={`sub-subscribe-${plan.id}`}
                  className={`mt-5 rounded-md h-11 font-semibold ${plan.highlight ? "bg-gold hover:bg-gold-hover text-ink" : "bg-ink hover:bg-ink/90 text-white"}`}>
                  {isCurrent ? "Plano atual" : freeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ativar grátis"}
                </Button>
              ) : (
                <Button onClick={() => subscribe(plan)} disabled={loadingKey === lk || isCurrent}
                  data-testid={`sub-subscribe-${plan.id}`}
                  className={`mt-5 rounded-md h-11 font-semibold ${plan.highlight ? "bg-gold hover:bg-gold-hover text-ink" : "bg-ink hover:bg-ink/90 text-white"}`}>
                  {isCurrent ? "Plano atual" : loadingKey === lk ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assinar"}
                </Button>
              )}
              <ul className="mt-5 space-y-2">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-gold" />
                    <span className={plan.highlight ? "text-cream/90" : "text-ink"}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyNote({ text }) {
  return <p className="text-cappuccino/70 text-sm bg-white border border-dashed border-border rounded-xl px-4 py-6 text-center">{text}</p>;
}
